/**
 * Global error suppression for Coveo AbortError and Next.js routing errors
 * This must be initialized early in the application lifecycle
 */

let isInitialized = false;

export const initializeCoveoErrorSuppression = () => {
  if (isInitialized || typeof window === 'undefined') {
    return;
  }

  isInitialized = true;

  const originalConsoleError = console.error;
  const originalConsoleWarn = console.warn;

  // Override console.error globally
  console.error = (...args: any[]) => {
    const shouldSuppress = args.some((arg: any) => {
      if (!arg) return false;

      // Handle string arguments
      if (typeof arg === 'string') {
        return (
          arg.includes('AbortError') ||
          arg.includes('signal is aborted without reason') ||
          arg.includes('search/executeSearch/rejected') ||
          arg.includes('search/fetchPage/rejected') ||
          arg.includes('Cancel rendering route') ||
          arg.includes('Abort fetching component')
        );
      }

      // Handle error objects with the exact structure from Coveo
      if (typeof arg === 'object') {
        // Check for Redux action structure
        if (arg.type?.includes('search/') && arg.type?.includes('rejected')) {
          return true;
        }

        // Check for error property
        if (arg.error?.name === 'AbortError') {
          return true;
        }

        // Check meta property
        if (arg.meta?.aborted === true || arg.meta?.requestStatus === 'rejected') {
          return true;
        }

        // Fallback: stringify and check
        try {
          const argString = JSON.stringify(arg);
          return (
            argString.includes('AbortError') ||
            argString.includes('signal is aborted') ||
            argString.includes('executeSearch/rejected') ||
            argString.includes('fetchPage/rejected') ||
            argString.includes('Cancel rendering route') ||
            argString.includes('Abort fetching component')
          );
        } catch {
          return false;
        }
      }

      return false;
    });

    if (!shouldSuppress) {
      originalConsoleError(...args);
    }
  };

  // Override console.warn
  console.warn = (...args: any[]) => {
    const shouldSuppress = args.some((arg: any) => {
      if (!arg) return false;
      if (typeof arg === 'string') {
        return (
          arg.includes('AbortError') ||
          arg.includes('signal is aborted') ||
          arg.includes('Cancel rendering route') ||
          arg.includes('Abort fetching component')
        );
      }
      if (typeof arg === 'object' && arg.error?.name === 'AbortError') {
        return true;
      }
      return false;
    });

    if (!shouldSuppress) {
      originalConsoleWarn(...args);
    }
  };

  // Global unhandled rejection handler
  const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
    const reason = event.reason;
    if (
      reason?.name === 'AbortError' ||
      reason?.message?.includes('signal is aborted') ||
      reason?.message?.includes('Cancel rendering route') ||
      reason?.message?.includes('Abort fetching component') ||
      (typeof reason === 'string' &&
        (reason.includes('Cancel rendering route') || reason.includes('Abort fetching component')))
    ) {
      event.preventDefault();
    }
  };

  window.addEventListener('unhandledrejection', handleUnhandledRejection);

  const handleError = (event: ErrorEvent) => {
    const message = event.message || '';
    if (
      message.includes('AbortError') ||
      message.includes('signal is aborted') ||
      message.includes('Cancel rendering route') ||
      message.includes('Abort fetching component')
    ) {
      event.preventDefault();
      return false;
    }
    return true;
  };

  window.addEventListener('error', handleError);

  // Store original functions for potential restoration
  (window as any).__originalConsoleError = originalConsoleError;
  (window as any).__originalConsoleWarn = originalConsoleWarn;
};

export const restoreCoveoErrorSuppression = () => {
  if (typeof window === 'undefined') return;

  if ((window as any).__originalConsoleError) {
    console.error = (window as any).__originalConsoleError;
  }
  if ((window as any).__originalConsoleWarn) {
    console.warn = (window as any).__originalConsoleWarn;
  }

  isInitialized = false;
};
